/**
 * Greedy AI Strategy (v1)
 *
 * AI implementation that selects moves based on immediate score evaluation.
 * Uses the move evaluation system to pick the highest-scoring move available.
 * Target response time: ~1 second for thorough evaluation.
 */

import { AIDifficulty, PlayerColor, BoardPosition } from '@/types/game-types';
import type { GameState } from '@/core/models/GameState';
import { AIStrategyBase, type AIMoveResult, AIUtils } from '../ai-strategy-base';
import { evaluateAllMoves, getBestMove } from '@/core/utils/move-evaluator';
import { Grade } from '@/types/game-types';

/**
 * Greedy AI implementation
 */
export class GreedyAI extends AIStrategyBase {
  public readonly difficulty = AIDifficulty.GREEDY;
  public readonly name = 'Greedy AI';
  public readonly description = 'Selects moves with highest immediate score';

  protected async selectMoveImpl(
    gameState: GameState,
    player: PlayerColor,
    timeLimit: number,
  ): Promise<AIMoveResult> {
    const startTime = Date.now();

    // Get all valid moves and evaluate them
    const evaluatedMoves = evaluateAllMoves(gameState.board as Uint8Array, new Map(gameState.validMoves), player);

    if (evaluatedMoves.length === 0) {
      throw new Error('No valid moves available');
    }

    // Simulate thinking time (but be efficient)
    const thinkingTime = Math.min(500 + Math.random() * 300, timeLimit * 0.8); // 500-800ms
    const remainingTime = thinkingTime - (Date.now() - startTime);

    if (remainingTime > 0) {
      await new Promise(resolve => setTimeout(resolve, remainingTime));
    }

    // Get the best move
    const bestMove = getBestMove(evaluatedMoves);

    if (!bestMove) {
      throw new Error('Could not determine best move');
    }

    // Calculate confidence based on move quality and competition
    const confidence = this.calculateConfidence(evaluatedMoves, bestMove);

    return {
      position: bestMove.position,
      confidence,
      evaluation: bestMove.score.points,
      thinkingTime: Date.now() - startTime,
      depth: 1, // Only evaluates immediate moves
      nodesEvaluated: evaluatedMoves.length,
    };
  }

  /**
   * Calculate confidence based on move quality and alternatives
   */
  private calculateConfidence(
    evaluatedMoves: Array<{ position: BoardPosition; score: any; flippedPositions: BoardPosition[] }>,
    bestMove: { position: BoardPosition; score: any; flippedPositions: BoardPosition[] },
  ): number {
    if (evaluatedMoves.length === 1) {
      return 0.9; // High confidence when only one move
    }

    // Base confidence from move grade
    let confidence = 0.3; // Start with moderate confidence

    switch (bestMove.score.grade) {
      case Grade.S:
        confidence = 0.95;
        break;
      case Grade.A:
        confidence = 0.8;
        break;
      case Grade.B:
        confidence = 0.6;
        break;
      case Grade.C:
        confidence = 0.3;
        break;
    }

    // Adjust based on score gap to second-best move
    const sortedMoves = [...evaluatedMoves].sort((a, b) => b.score.points - a.score.points);
    if (sortedMoves.length > 1) {
      const scoreGap = sortedMoves[0]!.score.points - sortedMoves[1]!.score.points;

      if (scoreGap > 15) {
        confidence += 0.15; // Clear winner
      } else if (scoreGap > 5) {
        confidence += 0.05; // Moderate advantage
      } else {
        confidence -= 0.1; // Close competition
      }
    }

    return Math.max(0.1, Math.min(0.95, confidence));
  }
}

/**
 * Enhanced Greedy AI with additional heuristics
 */
export class EnhancedGreedyAI extends AIStrategyBase {
  public readonly difficulty = AIDifficulty.GREEDY;
  public readonly name = 'Enhanced Greedy AI';
  public readonly description = 'Greedy with additional strategic considerations';

  protected async selectMoveImpl(
    gameState: GameState,
    player: PlayerColor,
    timeLimit: number,
  ): Promise<AIMoveResult> {
    const startTime = Date.now();

    const evaluatedMoves = evaluateAllMoves(gameState.board as Uint8Array, new Map(gameState.validMoves), player);

    if (evaluatedMoves.length === 0) {
      throw new Error('No valid moves available');
    }

    // Apply additional heuristics
    const enhancedMoves = evaluatedMoves.map(move => ({
      ...move,
      enhancedScore: this.calculateEnhancedScore(gameState, move, player),
    }));

    // Sort by enhanced score
    enhancedMoves.sort((a, b) => b.enhancedScore - a.enhancedScore);

    // Consider top candidates with similar scores
    const bestScore = enhancedMoves[0]!.enhancedScore;
    const threshold = bestScore * 0.9; // Within 10% of best
    const topCandidates = enhancedMoves.filter(move => move.enhancedScore >= threshold);

    // Add some randomization among top candidates to avoid predictability
    const selectedMove = AIUtils.selectWeightedRandom(
      topCandidates.map(move => ({ ...move, score: move.enhancedScore })),
      2.0, // Temperature for selection
    );

    // Simulate thinking time
    const targetThinkingTime = Math.min(600 + Math.random() * 400, timeLimit * 0.9);
    const remainingTime = targetThinkingTime - (Date.now() - startTime);

    if (remainingTime > 0) {
      await new Promise(resolve => setTimeout(resolve, remainingTime));
    }

    const confidence = this.calculateEnhancedConfidence(topCandidates, selectedMove);

    return {
      position: selectedMove.position,
      confidence,
      evaluation: selectedMove.enhancedScore,
      thinkingTime: Date.now() - startTime,
      depth: 1,
      nodesEvaluated: evaluatedMoves.length,
    };
  }

  /**
   * Calculate enhanced score with additional heuristics
   */
  private calculateEnhancedScore(
    gameState: GameState,
    move: { position: BoardPosition; score: any; flippedPositions: BoardPosition[] },
    player: PlayerColor,
  ): number {
    let score = move.score.points;

    // Game phase consideration
    const gamePhase = AIUtils.getGamePhase(gameState);

    if (gamePhase > 0.7) {
      // Endgame: prioritize flipped pieces over position
      score += move.flippedPositions.length * 2;
    } else if (gamePhase < 0.3) {
      // Early game: be more careful about dangerous positions
      if (AIUtils.isDangerousPosition(move.position)) {
        score -= 5;
      }
    }

    // Stability consideration
    const stability = AIUtils.evaluatePositionStability(gameState, move.position, player);
    score += stability;

    // Mobility preservation (don't give opponent too many options)
    const opponent = player === PlayerColor.BLACK ? PlayerColor.WHITE : PlayerColor.BLACK;
    const currentMobility = AIUtils.calculateMobility(gameState, opponent);

    // Slightly prefer moves that don't dramatically increase opponent mobility
    if (currentMobility < 3) {
      score += 2; // Bonus for limiting opponent when they're already constrained
    }

    return score;
  }

  /**
   * Calculate confidence for enhanced AI
   */
  private calculateEnhancedConfidence(
    topCandidates: any[],
    selectedMove: any,
  ): number {
    let confidence = 0.6; // Base confidence for enhanced greedy

    // Confidence increases if we have fewer but stronger candidates
    if (topCandidates.length === 1) {
      confidence = 0.9;
    } else if (topCandidates.length <= 3) {
      confidence = 0.8;
    }

    // Adjust based on selected move grade
    if (selectedMove.score?.grade) {
      switch (selectedMove.score.grade) {
        case Grade.S:
          confidence = Math.max(confidence, 0.9);
          break;
        case Grade.A:
          confidence = Math.max(confidence, 0.75);
          break;
        case Grade.B:
          confidence = Math.max(confidence, 0.6);
          break;
      }
    }

    return Math.max(0.2, Math.min(0.95, confidence));
  }
}

/**
 * Adaptive Greedy AI that adjusts strategy based on game situation
 */
export class AdaptiveGreedyAI extends AIStrategyBase {
  public readonly difficulty = AIDifficulty.GREEDY;
  public readonly name = 'Adaptive Greedy AI';
  public readonly description = 'Greedy AI that adapts strategy to game phase and score';

  protected async selectMoveImpl(
    gameState: GameState,
    player: PlayerColor,
    timeLimit: number,
  ): Promise<AIMoveResult> {
    const startTime = Date.now();

    const evaluatedMoves = evaluateAllMoves(gameState.board as Uint8Array, new Map(gameState.validMoves), player);

    if (evaluatedMoves.length === 0) {
      throw new Error('No valid moves available');
    }

    // Determine strategy based on game situation
    const strategy = this.determineStrategy(gameState, player);

    // Apply strategy-specific evaluation
    const strategicMoves = evaluatedMoves.map(move => ({
      ...move,
      strategicScore: this.calculateStrategicScore(gameState, move, player, strategy),
    }));

    strategicMoves.sort((a, b) => b.strategicScore - a.strategicScore);

    // Select best move with some randomization based on strategy
    const selectionTemperature = this.getSelectionTemperature(strategy);
    const topMoves = strategicMoves.slice(0, Math.min(5, strategicMoves.length));

    const selectedMove = AIUtils.selectWeightedRandom(
      topMoves.map(move => ({ ...move, score: move.strategicScore })),
      selectionTemperature,
    );

    // Adaptive thinking time based on situation complexity
    const adaptiveThinkingTime = this.calculateAdaptiveThinkingTime(
      gameState,
      evaluatedMoves.length,
      strategy,
      timeLimit,
    );

    const remainingTime = adaptiveThinkingTime - (Date.now() - startTime);
    if (remainingTime > 0) {
      await new Promise(resolve => setTimeout(resolve, remainingTime));
    }

    return {
      position: selectedMove.position,
      confidence: this.calculateAdaptiveConfidence(strategy, selectedMove, topMoves),
      evaluation: selectedMove.strategicScore,
      thinkingTime: Date.now() - startTime,
      depth: 1,
      nodesEvaluated: evaluatedMoves.length,
    };
  }

  /**
   * Determine strategy based on game situation
   */
  private determineStrategy(gameState: GameState, player: PlayerColor): string {
    const gamePhase = AIUtils.getGamePhase(gameState);
    const myScore = player === PlayerColor.BLACK ? gameState.scores.black : gameState.scores.white;
    const opponentScore = player === PlayerColor.BLACK ? gameState.scores.white : gameState.scores.black;
    const scoreDifference = myScore - opponentScore;
    const mobility = gameState.validMoves.size;

    // Endgame strategy
    if (gamePhase > 0.8) {
      return scoreDifference > 0 ? 'endgame_ahead' : 'endgame_behind';
    }

    // Mid-game strategies
    if (gamePhase > 0.4) {
      if (scoreDifference > 5) {return 'consolidate';}
      if (scoreDifference < -5) {return 'aggressive';}
      if (mobility < 3) {return 'careful';}
      return 'balanced';
    }

    // Early game strategies
    if (mobility > 8) {return 'opportunistic';}
    return 'cautious';
  }

  private calculateStrategicScore(
    gameState: GameState,
    move: any,
    player: PlayerColor,
    strategy: string,
  ): number {
    let score = move.score.points;
    AIUtils.getGamePhase(gameState);

    switch (strategy) {
      case 'endgame_ahead':
        // When ahead in endgame, maximize piece count
        score += move.flippedPositions.length * 3;
        break;

      case 'endgame_behind':
        // When behind in endgame, take risks for big gains
        score += move.flippedPositions.length * 4;
        if (move.score.grade === Grade.A || move.score.grade === Grade.S) {
          score += 10;
        }
        break;

      case 'aggressive':
        // Prioritize high-scoring moves when behind
        if (move.score.grade === Grade.A || move.score.grade === Grade.S) {
          score += 8;
        }
        score += move.flippedPositions.length * 2;
        break;

      case 'consolidate':
        // Play safely when ahead
        if ([0, 7, 56, 63].includes(move.position)) {
          score += 10; // Corner bonus
        }
        if (AIUtils.isDangerousPosition(move.position)) {
          score -= 8; // Avoid risk
        }
        break;

      case 'careful':
        // Low mobility - be extra careful
        if (AIUtils.isDangerousPosition(move.position)) {
          score -= 15;
        }
        score += AIUtils.evaluatePositionStability(gameState, move.position, player) * 2;
        break;

      case 'opportunistic':
        // High mobility - can take some risks for rewards
        if (move.score.grade === Grade.S) {
          score += 15;
        }
        break;

      case 'cautious':
        // Early game caution
        if (AIUtils.isDangerousPosition(move.position)) {
          score -= 10;
        }
        break;

      default: // 'balanced'
        // Standard evaluation
        break;
    }

    return score;
  }

  private getSelectionTemperature(strategy: string): number {
    switch (strategy) {
      case 'aggressive':
      case 'endgame_behind':
        return 1.5; // More randomization when desperate
      case 'careful':
      case 'consolidate':
        return 3.0; // Less randomization when being careful
      default:
        return 2.0; // Moderate randomization
    }
  }

  private calculateAdaptiveThinkingTime(
    _gameState: GameState,
    moveCount: number,
    strategy: string,
    timeLimit: number,
  ): number {
    let baseTime = 400;

    // More thinking for complex situations
    if (moveCount > 8) {baseTime += 200;}
    if (moveCount < 3) {baseTime += 100;} // Think more when few options

    // Strategy-based adjustments
    switch (strategy) {
      case 'endgame_ahead':
      case 'endgame_behind':
        baseTime += 300; // Endgame needs more thought
        break;
      case 'careful':
        baseTime += 200; // Be more careful
        break;
      case 'opportunistic':
        baseTime -= 100; // Can be quicker with many options
        break;
    }

    // Add randomization
    baseTime += Math.random() * 200;

    return Math.min(baseTime, timeLimit * 0.9);
  }

  private calculateAdaptiveConfidence(strategy: string, selectedMove: any, topMoves: any[]): number {
    let confidence = 0.7; // Base confidence

    // Strategy-based confidence
    switch (strategy) {
      case 'consolidate':
      case 'endgame_ahead':
        confidence = 0.8; // More confident when ahead
        break;
      case 'aggressive':
      case 'endgame_behind':
        confidence = 0.6; // Less confident when taking risks
        break;
      case 'careful':
        confidence = 0.75; // Moderate confidence in careful play
        break;
    }

    // Adjust based on move quality
    if (selectedMove.score?.grade === Grade.S) {
      confidence = Math.min(0.95, confidence + 0.15);
    } else if (selectedMove.score?.grade === Grade.A) {
      confidence = Math.min(0.9, confidence + 0.1);
    }

    // Reduce confidence if many similar options
    if (topMoves.length > 4) {
      confidence -= 0.1;
    }

    return Math.max(0.2, confidence);
  }
}

/**
 * Factory function to create greedy AI variants
 */
export const createGreedyAI = {
  /**
   * Standard greedy AI
   */
  standard(): GreedyAI {
    return new GreedyAI();
  },

  /**
   * Enhanced greedy with additional heuristics
   */
  enhanced(): EnhancedGreedyAI {
    return new EnhancedGreedyAI();
  },

  /**
   * Adaptive greedy that changes strategy based on game state
   */
  adaptive(): AdaptiveGreedyAI {
    return new AdaptiveGreedyAI();
  },
};